import { useCallback, useContext, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Accordion, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ActionButton from "../formInputs/buttons/ActionButton";
import DcsdDialog from "../modals/DcsdDialog";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import GroupAccordionItem from "./groupAccordion/GroupAccordionItem";
import GuardianEprDao from "../../dao/GuardianEprDao";
import isGradingProfileWindowOpen from "../../utils/ActionControls/isGradingProfileWindowOpen";
import isEprEditable from "../../utils/ActionControls/isEprEditable";
import { canAdminBypassGradingWindow } from "../../utils/isUser";
import LoadingSvg from "../loadingSvg/LoadingSvg";
import MostRecentEprs from "../guardian/MostRecentEprs";
import RatingProgressBar from "./RatingProgressBar";
import RadioSwitchButton from "../formInputs/buttons/RadioSwitchButton";
import StudentRosterTable from "./StudentRosterTable";

import { GlobalContext } from "../contextProvider/ContextProvider";
import { formatDateAndTime, getTodayWithTime } from "../../utils/DateFormatter";

import "../../styles/GradingAccordion.scss";

/**
 * GradingAccordionItem component represents an expandable panel for a grading profile.
 * It allows users to manage comments, subjects, and the roster for a specific grade.
 *
 * @component
 * @param {Function} dispatch
 * @param {Object} grade
 * @param {Object} gradingProfile
 * @param {Boolean} isHistoricalView
 * @param {string} ownerDisplayName
 * @param {Function} setSavedMessage
 * @param {Function} setShowSaved
 * @param {Object} state
 * @returns {JSX.Element}
 */
const GradingAccordionItem = ({
    dispatch,
    grade,
    gradingProfile,
    isHistoricalView = false,
    ownerDisplayName,
    setSavedMessage,
    setShowSaved,
    state,
    basePath
}) => {
    const navigate = useNavigate();
    const isFirstRender = useRef(true);
    const saveTimerRef = useRef(null);

    const { state: globalState } = useContext(GlobalContext);
    const { actionWindowsDto, userDetails, token } = globalState || {};

    const [isExpanded, setIsExpanded] = useState(false);
    const [open, setOpen] = useState("false");
    const [profileData, setProfileData] = useState(gradingProfile);
    const [showProfileComment, setShowProfileComment] = useState(true);
    const [ratingCounts, setRatingCounts] = useState(null);
    const [groupRatingCounts, setGroupRatingCounts] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isActiveWindow, setIsActiveWindow] = useState(false);

    const [previewShow, setPreviewShow] = useState(false);
    const [previewStudentProfileData, setPreviewStudentProfileData] = useState(null);
    const [previewLoader, setPreviewLoader] = useState(false);
    const [viewMode, setViewMode] = useState("EPR");

    const handleViewModeChange = (e) => {
        setViewMode(e.target.value);
    };
    const initialSubjectVisibility = grade?.subjectToStandards.reduce((acc, subject) => {
        acc[subject.templateId] = false;
        return acc;
    }, {});
    const [subjectCommentsVisible, setSubjectCommentsVisible] = useState(initialSubjectVisibility);

    const studentProfileCacheRef = useRef({}); // { [gradingProfileStudentId]: studentProfileDataDto }

    const getStudentProfileData = useCallback(
        async (gradingProfileStudentId) => {
            if (!gradingProfileStudentId || !token) return null;

            // cache hit
            if (studentProfileCacheRef.current[gradingProfileStudentId]) {
                return studentProfileCacheRef.current[gradingProfileStudentId];
            }

            try {
                const options = {
                    action: "getStudentProfileData",
                    gradingProfileStudentId,
                    token
                };

                const response = await ElementaryProgressReportDao(options);
                const dto = response?.data?.payload || null;

                if (dto) {
                    studentProfileCacheRef.current[gradingProfileStudentId] = dto;
                }

                return dto;
            } catch (e) {
                toast.error("There was a problem loading student data.");
                console.error(e);
                return null;
            }
        },
        [token]
    );

    const handlePreviewStudent = useCallback(
        async (student) => {
            if (!student?.key || !token) return;

            try {
                setPreviewLoader(true);

                const dto = await getStudentProfileData(student.key);

                if (dto) {
                    setPreviewStudentProfileData(dto);
                    setPreviewShow(true);
                } else {
                    toast.error("Unable to load Report Card preview.");
                }
            } catch (e) {
                console.error(e);
                toast.error("Unable to load Report Card preview.");
            } finally {
                setPreviewLoader(false);
            }
        },
        [getStudentProfileData, token]
    );

    const handleDownloadStudentPdf = useCallback(
        async (student) => {
            const studentKey = student?.key || student.gradingProfileStudentId;
            if (!studentKey) return;

            try {
                setPreviewLoader(true); // or a separate per-row loader if you want

                const dto = await getStudentProfileData(studentKey);
                if (!dto) {
                    toast.error("There was a problem loading student data.");
                    return;
                }

                const options = {
                    action: "eprPdfDownload",
                    gradingProfileId: dto.gradingProfileId,
                    studentNumber: dto.studentNumber,
                    token
                };

                const nameArray = (dto.studentName || "").split(", ");
                const lastName = nameArray?.[0] || "student";
                const firstName = nameArray?.[1] || "";

                const response = await GuardianEprDao(options);

                if (response?.data) {
                    const blob = new Blob([response.data], { type: "application/pdf" });
                    const url = globalThis.URL.createObjectURL(blob);

                    window.open(url, "_blank", "noopener,noreferrer");

                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `EPR_${lastName}_${firstName}_${dto.studentNumber}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();

                    globalThis.URL.revokeObjectURL(url);

                    toast.success("Report Card pdf downloaded.", { autoClose: 3000 });
                } else {
                    toast.error("Unable to download Report Card.");
                }
            } catch (e) {
                console.error(e);
                toast.error("Unable to download Report Card.");
            } finally {
                setPreviewLoader(false);
            }
        },
        [getStudentProfileData, token]
    );

    /**
     * Handles saving the grading profile comments to the database.
     */
    const handleSave = useCallback(() => {
        // eslint-disable-next-line no-unused-vars
        const { gradingProfileStudents, ...dataToSave } = profileData;
        const options = {
            action: "updateGradingProfileComments",
            gradingProfileId: profileData.key,
            data: { ...dataToSave, lastUpdaterGuid: userDetails.uid },
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const updatedProfile = response.data.payload;
                    setShowSaved(true);
                    setSavedMessage(`Saved: ${getTodayWithTime()}`);
                    dispatch({
                        type: "SET_PROFILES",
                        payload: state.gradingProfiles.map((profile) =>
                            profile.key === updatedProfile.key ? updatedProfile : profile
                        )
                    });
                    setIsDirty(false);
                } else {
                    toast.error("Comments were not saved. Please make note of your comments and try again later.");
                }
            })
            .catch((error) => {
                console.error(error);
                toast.error("Comments were not saved. Please make note of your comments and try again later.");
            });
    }, [profileData, token, dispatch, setShowSaved, setSavedMessage, state.gradingProfiles, userDetails.uid]);

    /**
     * Navigates to the Manage Roster page for the selected grading profile.
     */
    const handleManageRosterClick = () => {
        dispatch({ type: "SET_SELECTED_EPR", payload: gradingProfile });
        navigate(`${basePath || "/home"}/summary/roster/${gradingProfile.key}`);
    };

    /**
     * Navigates to the homeroom group page for the selected grading profile.
     */
    const handleHomeroomGroupClick = () => {
        dispatch({ type: "SET_SELECTED_EPR", payload: gradingProfile });
        navigate(`${basePath || "/home"}/summary/roster/${gradingProfile.key}/groups`);
    };

    /**
     * Navigates to the homeroom group page for the selected grading profile.
     */
    const handleShareGroupClick = () => {
        dispatch({ type: "SET_SELECTED_EPR", payload: gradingProfile });
        navigate(`${basePath || "/home"}/summary/share/${gradingProfile.key}`);
    };

    const toggleAccordion = () => {
        setIsExpanded(!isExpanded);
    };

    /**
     * Handles updating the profile comment text.
     * @param {Object} e
     */
    const handleProfileCommentChange = (e) => {
        setIsDirty(true);
        setProfileData({
            ...profileData,
            comment: e.target.value
        });
    };

    /**
     * Handles updating the profile comment text.
     * @param {Object} e
     */
    const handleSleCommentChange = (e) => {
        setIsDirty(true);
        setProfileData({
            ...profileData,
            sleComment: e.target.value
        });
    };

    /**
     * Handles updating subject-specific comments.
     * @param {number} templateId
     * @param {Object} e
     */
    const handleSubjectCommentChange = (templateId, e) => {
        setIsDirty(true);
        const updatedCriteriaList = profileData.gradingCriteriaList.map((item) => {
            if (item.parentTemplate === templateId) {
                return { ...item, comment: e.target.value };
            }
            return item;
        });
        setProfileData({
            ...profileData,
            gradingCriteriaList: updatedCriteriaList
        });
    };

    /**
     * Toggles the visibility of subject-specific comments.
     * @param {number} templateId
     */
    const toggleSubjectCommentVisibility = (templateId) => {
        setSubjectCommentsVisible((prev) => ({
            ...prev,
            [templateId]: !prev[templateId]
        }));
    };

    /**
     * Handles releasing/unreleasing (until grading profile action control window closes) the grading profile and updates global state accordingly.
     */
    const handleToggleRelease = () => {
        const options = {
            action: "updateGradingProfileStatus",
            gradingProfileId: gradingProfile.key,
            releaseStatus: gradingProfile.status === "READY_FOR_RELEASE" ? "NOT_RELEASED" : "READY_FOR_RELEASE",
            guid: userDetails?.uid ?? null,
            token
        };
        dispatch({ type: "SET_LOADER", payload: true });

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response) {
                    const updatedProfile = response.data.payload;
                    dispatch({
                        type: "SET_PROFILES",
                        payload: state.gradingProfiles.map((profile) =>
                            profile.key === updatedProfile.key ? updatedProfile : profile
                        )
                    });
                    if (String(state.selectedEpr?.key) === String(updatedProfile.key)) {
                        dispatch({ type: "SET_SELECTED_EPR", payload: updatedProfile });
                    }
                    setProfileData((prev) => ({ ...prev, ...updatedProfile }));
                    toast.success(
                        `Report Card has been ${updatedProfile.status === "READY_FOR_RELEASE" ? "released" : "unreleased"}.`
                    );
                } else {
                    toast.error("Failed to update Report Card status.", { autoClose: false });
                }
            })
            .catch((error) => {
                toast.error("Failed to update Report Card status.", { autoClose: false });
                console.error(error);
            })
            .finally(() => {
                dispatch({ type: "SET_LOADER", payload: false });
            });
    };

    /**
     * Handles deleting the grading profile and updates global state accordingly.
     */
    const handleDelete = () => {
        const options = {
            action: "deleteGradingProfile",
            gradingProfileId: gradingProfile.key,
            params: {
                lastUpdaterGuid: userDetails.uid
            },
            token
        };
        dispatch({ type: "SET_LOADER", payload: true });

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response) {
                    dispatch({
                        type: "SET_PROFILES",
                        payload: state.gradingProfiles.filter((profile) => profile.key !== gradingProfile.key)
                    });
                    dispatch({
                        type: "SET_SELECTED_EPR",
                        payload: ""
                    });
                    toast.success("Report Card successfully deleted.", { autoClose: 3000 });
                    setOpen("false");
                } else {
                    toast.error("Failed to delete Report Card", { autoClose: false });
                }
            })
            .catch((error) => {
                toast.error("Failed to delete Report Card", { autoClose: false });
                console.error(error);
            })
            .finally(() => {
                dispatch({ type: "SET_LOADER", payload: false });
            });
    };

    /**
     * Deletes a homeroom group by key and updates local and global state.
     *
     * @param {string} groupKey - The key of the group to be deleted.
     */
    const handleDeleteGroup = (groupKey) => {
        const options = {
            action: "deleteHomeroomGroup",
            homeroomGroupId: groupKey,
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data === "") {
                    const updatedGroups = gradingProfile.homeroomGroups.filter((group) => group.key !== groupKey);

                    const updatedEpr = {
                        ...gradingProfile,
                        homeroomGroups: updatedGroups
                    };

                    const updatedProfileData = {
                        ...profileData,
                        homeroomGroups: updatedGroups
                    };

                    setProfileData(updatedProfileData);
                    dispatch({ type: "SET_SELECTED_EPR", payload: updatedEpr });
                    dispatch({
                        type: "SET_PROFILES",
                        payload: state.gradingProfiles.map((profile) =>
                            profile.key === updatedEpr.key ? updatedEpr : profile
                        )
                    });

                    toast.success("Report Card homeroom group deleted.", { autoClose: 3000 });
                }
            })
            .catch((error) => {
                console.error(error);
                toast.error("Report Card homeroom group could not be deleted.");
            });
    };

    /**
     * Handles saving profile data (comments) with a delay to reduce API calls.
     */
    useEffect(() => {
        if (!isDirty || isHistoricalView) return;
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
            handleSave();
            setIsDirty(false);
        }, 3000);
        return () => clearTimeout(saveTimerRef.current);
    }, [handleSave, isDirty, isHistoricalView, profileData]);

    /**
     * Gets Rating Counts by subject for homeroom roster
     */
    useEffect(() => {
        if (!gradingProfile || isHistoricalView || !token) return;

        const options = {
            action: "getRatingCounts",
            gradingProfileId: gradingProfile.key,
            token
        };
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    setRatingCounts(response.data.payload);
                }
            })
            .catch((error) => {
                console.error(error);
                toast.error("There was a problem loading rating counts.");
            });
    }, [gradingProfile, isHistoricalView, token]);

    /**
     * Gets Rating Counts by subject for groups
     */
    useEffect(() => {
        if (!gradingProfile || isHistoricalView || !token) return;

        const options = {
            action: "getGroupRatingCounts",
            gradingProfileId: gradingProfile.key,
            token
        };
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    setGroupRatingCounts(response.data.payload);
                }
            })
            .catch((error) => {
                console.error(error);
                toast.error("There was a problem loading rating counts.");
            });
    }, [gradingProfile, isHistoricalView, token]);

    useEffect(() => {
        if (actionWindowsDto && gradingProfile) {
            const profileTimePeriod = gradingProfile.timePeriod;

            const isWindowOpen = isGradingProfileWindowOpen(actionWindowsDto, profileTimePeriod);
            setIsActiveWindow(isWindowOpen);
        }
    }, [actionWindowsDto, gradingProfile]);
    const adminCanCancelRelease = canAdminBypassGradingWindow(userDetails);
    const isActiveAndIsUnreleased = isEprEditable({
        isActiveWindow,
        status: profileData?.status,
        userDetails
    });
    // Delete stays strictly window-gated (destructive) even for admins.
    const canDeleteEpr = isActiveWindow && profileData?.status === "NOT_RELEASED";

    const filteredGroups = profileData?.homeroomGroups?.filter((g) => !g.name.includes("INTERNAL_")) || [];
    const filteredGroupLength = filteredGroups.length;
    const currentParentWindow = actionWindowsDto?.find(
        (window) =>
            window.action === "EPR_PARENT_VIEW" && Number(window.differentiator) === Number(gradingProfile.timePeriod)
    );
    const currentGradingProfileWindow = actionWindowsDto?.find(
        (window) =>
            window.action === "GRADING_PROFILE" && Number(window.differentiator) === Number(gradingProfile.timePeriod)
    );

    if (isHistoricalView) {
        return (
            <div className="grading-accordion">
                <div
                    className={`acc-header ${isExpanded ? "expanded" : "collapsed"}`}
                    onClick={toggleAccordion}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleAccordion();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                >
                    <div>
                        <span className="owner-name">{ownerDisplayName}</span> –{" "}
                        <span className="teacher-epr-grade-name">{profileData.gradeName}</span> (
                        {gradingProfile.gradingProfileStudents.length} Students)
                    </div>
                    <div className="accordion-icon">
                        <i className={`${isExpanded ? "bi bi-chevron-down" : "bi bi-chevron-right"} h3`} />
                    </div>
                </div>
                {isExpanded && (
                    <div className="accordion-body">
                        <StudentRosterTable
                            students={gradingProfile.gradingProfileStudents}
                            schoolYearKey={gradingProfile?.schoolYearKey}
                            token={token}
                            loadImages={isExpanded}
                            renderActions={(student) => (
                                <>
                                    <ActionButton
                                        label="View Report Card"
                                        onClick={() => handlePreviewStudent(student)}
                                    />
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            student.pdfProcessed === "Y" ? (
                                                /* eslint-disable-next-line react/jsx-no-useless-fragment */
                                                <></>
                                            ) : (
                                                <Tooltip id={`pdf-tooltip-${student.key}`}>
                                                    Report Card PDF is not ready.
                                                    <br />
                                                    Please check back soon.
                                                </Tooltip>
                                            )
                                        }
                                    >
                                        <span style={{ display: "inline-block" }}>
                                            <ActionButton
                                                label="Download PDF"
                                                disable={student.pdfProcessed !== "Y"}
                                                onClick={() => handleDownloadStudentPdf(student)}
                                            />
                                        </span>
                                    </OverlayTrigger>
                                </>
                            )}
                        />
                    </div>
                )}
                <Modal show={previewShow} onHide={() => setPreviewShow(false)} size="xl" backdrop="static">
                    <Modal.Header closeButton />
                    <Modal.Body>
                        <div className="guardian-gutter">
                            {previewShow && (
                                <MostRecentEprs
                                    eprs={previewStudentProfileData ? [previewStudentProfileData] : []}
                                    loader={previewLoader}
                                    downloadPdf={() => handleDownloadStudentPdf(previewStudentProfileData)}
                                    trackGA={false}
                                />
                            )}
                        </div>
                    </Modal.Body>
                </Modal>
            </div>
        );
    }

    if (!grade) {
        return <div>There was an error loading Report Cards. Please check back later.</div>;
    }

    return (
        <div className="grading-accordion">
            <div
                className={`acc-header ${isExpanded ? "expanded" : "collapsed"}`}
                onClick={toggleAccordion}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault(); // prevent space from scrolling
                        toggleAccordion();
                    }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
            >
                <div>
                    <span className="owner-name">{ownerDisplayName}</span> -{" "}
                    <span className="teacher-epr-grade-name">{profileData.gradeName}</span> (
                    {profileData.gradingProfileStudents.length} Students)
                </div>
                <div className="accordion-icon">
                    <i className={`${isExpanded ? "bi bi-chevron-down" : "bi bi-chevron-right"} h3`} />
                </div>
            </div>
            {isExpanded && (
                <div className="accordion-body">
                    <div className="epr-switch-label">Select View:</div>
                    <div className="epr-view-switch">
                        <RadioSwitchButton
                            id={`view-mode-${gradingProfile.key}`}
                            name="viewMode"
                            leftLabel="Report Card Overview"
                            leftValue="EPR"
                            rightLabel="Student Overview"
                            rightValue="STUDENTS"
                            selectedValue={viewMode}
                            onChange={handleViewModeChange}
                        />
                    </div>
                    {viewMode === "EPR" && (
                        <>
                            <hr />
                            <div className="accordion-btn-nav-wrapper">
                                <ActionButton label="Share" onClick={() => handleShareGroupClick()} />
                                <ActionButton label="Manage Roster" onClick={() => handleManageRosterClick()} />
                                <ActionButton
                                    label={
                                        filteredGroupLength > 0
                                            ? "Manage Homeroom Group(s)"
                                            : "Create Homeroom Group(s)"
                                    }
                                    onClick={() => handleHomeroomGroupClick()}
                                />
                                <ActionButton
                                    className="action-button-delete"
                                    disable={!canDeleteEpr}
                                    label="Delete Report Card"
                                    onClick={() => setOpen("confirm-delete-homeroom-epr")}
                                />
                                <ActionButton
                                    label={gradingProfile.status === "NOT_RELEASED" ? "Release" : "Cancel Release"}
                                    disable={
                                        (!isActiveWindow &&
                                            profileData.status === "READY_FOR_RELEASE" &&
                                            !adminCanCancelRelease) ||
                                        profileData.gradingProfileStudents.length < 1
                                    }
                                    onClick={() =>
                                        profileData.status === "READY_FOR_RELEASE"
                                            ? setOpen("confirm-release-cancel")
                                            : setOpen("confirm-release")
                                    }
                                />
                            </div>

                            <div style={{ textAlign: "right", marginBottom: "-16px", marginTop: "32px" }}>
                                <ActionButton
                                    disable={!isActiveAndIsUnreleased}
                                    label="Save Overall and Subject Comments"
                                    onClick={() => handleSave()}
                                />
                            </div>
                            <div className="profile-comment-section">
                                <div
                                    className={`profile-comment-input-wrapper ${showProfileComment ? "show" : "hide"}`}
                                >
                                    <label htmlFor="profile-comment">
                                        This is an overall comment that will appear for every student on your homeroom
                                        Report Card:
                                    </label>
                                    <textarea
                                        id="profile-comment"
                                        disabled={!isActiveAndIsUnreleased}
                                        value={profileData.comment || ""}
                                        onChange={handleProfileCommentChange}
                                        placeholder="Enter overall comment for all students"
                                        className="profile-comment-textarea"
                                    />
                                </div>

                                <div style={{ width: "100%", display: "flex", justifyContent: "flex-start" }}>
                                    <button
                                        onClick={() => setShowProfileComment(!showProfileComment)}
                                        className="toggle-comment-btn"
                                    >
                                        {showProfileComment ? "Hide " : "View "} Comments for All Students
                                    </button>
                                </div>
                            </div>
                            <hr />
                            <div className="epr-subjects">
                                <div className="subject-card">
                                    <div className="subject-header" style={{ minHeight: "49px" }}>
                                        <span className="teacher-epr-subject-name">SLE</span>
                                        <div className="rating-count-container"></div>

                                        <div className="subject-header-right">
                                            <ActionButton
                                                label="Input Ratings"
                                                className="action-button-reg"
                                                onClick={() => {
                                                    const path = `${basePath || "/home"}/summary/input-ratings/homeroom/${gradingProfile.key}`;
                                                    navigate(path, {
                                                        state: {
                                                            gradeId: gradingProfile.grade,
                                                            sleSelected: true
                                                        }
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <button
                                        className="toggle-subject-comment-btn"
                                        onClick={() => toggleSubjectCommentVisibility("SLE")}
                                    >
                                        {subjectCommentsVisible["SLE"] ? "Hide " : "View "}
                                        Comment
                                    </button>
                                    {subjectCommentsVisible["SLE"] && (
                                        <div
                                            className={`subject-comment-wrapper ${subjectCommentsVisible["SLE"] ? "show" : "hide"}`}
                                        >
                                            <label htmlFor={`subject-comment-SLE`} className="visually-hidden">
                                                Add comment for SLE
                                            </label>
                                            <textarea
                                                id={`subject-comment-SLE`}
                                                disabled={!isActiveAndIsUnreleased}
                                                value={profileData.sleComment || ""}
                                                onChange={handleSleCommentChange}
                                                placeholder="Add comment for SLE"
                                                className="subject-comment-textarea"
                                            />
                                        </div>
                                    )}
                                </div>
                                {grade.subjectToStandards.map((subject) => {
                                    const criteria = profileData.gradingCriteriaList.find(
                                        (item) => item.parentTemplate === subject.templateId
                                    );
                                    const countObject = ratingCounts?.find(
                                        (obj) => obj.gradingCriteriaId === Number.parseInt(criteria.key, 10)
                                    );

                                    return (
                                        <div key={subject.templateId} className="subject-card">
                                            <div className="subject-header" style={{ minHeight: "49px" }}>
                                                <span className="teacher-epr-subject-name">{subject.subjectName}</span>
                                                <div className="rating-count-container">
                                                    <div>
                                                        {countObject ? (
                                                            <RatingProgressBar countObject={countObject} />
                                                        ) : null}
                                                    </div>
                                                </div>
                                                <div className="subject-header-right">
                                                    <ActionButton
                                                        label="Input Ratings"
                                                        className="action-button-reg"
                                                        onClick={() => {
                                                            const path = `${basePath || "/home"}/summary/input-ratings/homeroom/${gradingProfile.key}`;
                                                            navigate(path, {
                                                                state: {
                                                                    subjectId: subject.templateId,
                                                                    subjectName: subject.subjectName,
                                                                    gradeId: gradingProfile.grade,
                                                                    selectedEpr: gradingProfile
                                                                }
                                                            });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                className="toggle-subject-comment-btn"
                                                onClick={() => toggleSubjectCommentVisibility(subject.templateId)}
                                            >
                                                {subjectCommentsVisible[subject.templateId] ? "Hide " : "View "}
                                                Comment
                                            </button>
                                            {subjectCommentsVisible[subject.templateId] && (
                                                <div
                                                    className={`subject-comment-wrapper ${subjectCommentsVisible[subject.templateId] ? "show" : "hide"}`}
                                                >
                                                    <label
                                                        htmlFor={`subject-comment-${subject.templateId}`}
                                                        className="visually-hidden"
                                                    >
                                                        {`Add comment for ${subject.subjectName}`}
                                                    </label>
                                                    <textarea
                                                        id={`subject-comment-${subject.templateId}`}
                                                        disabled={!isActiveAndIsUnreleased}
                                                        value={criteria ? criteria.comment || "" : ""}
                                                        onChange={(e) =>
                                                            handleSubjectCommentChange(subject.templateId, e)
                                                        }
                                                        placeholder={`Add comment for ${subject.subjectName}`}
                                                        className="subject-comment-textarea"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {filteredGroups.length > 0 && (
                                <>
                                    <hr style={{ marginTop: "2.5rem" }} />
                                    <div className="epr-group-section">
                                        <h5 className="group-section-title">My Homeroom Groups</h5>
                                        <div className="group-acc-wrapper">
                                            {filteredGroups.map((group) => (
                                                <Accordion key={group.key}>
                                                    <GroupAccordionItem
                                                        key={group.key}
                                                        dispatch={dispatch}
                                                        eventKey={String(group.key)}
                                                        grade={grade}
                                                        group={group}
                                                        groupRatingCounts={groupRatingCounts}
                                                        handleDeleteGroup={handleDeleteGroup}
                                                        isActiveAndIsUnreleased={isActiveAndIsUnreleased}
                                                        onEdit={() => {
                                                            navigate(
                                                                `${basePath || "/home"}/summary/roster/${gradingProfile.key}/groups`,
                                                                {
                                                                    state: { openGroupKey: group.key }
                                                                }
                                                            );
                                                        }}
                                                        selectedEpr={profileData}
                                                        basePath={basePath}
                                                    />
                                                </Accordion>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    {viewMode === "STUDENTS" && (
                        <>
                            <hr />

                            <StudentRosterTable
                                students={profileData.gradingProfileStudents}
                                schoolYearKey={gradingProfile?.schoolYearKey}
                                token={token}
                                loadImages={isExpanded}
                                renderActions={(student) => (
                                    <ActionButton
                                        label={isActiveAndIsUnreleased ? "Input Ratings" : "View Ratings"}
                                        onClick={() => {
                                            const viewPath = "/summary/input-ratings/student";
                                            const targetPath = basePath
                                                ? `${basePath}${viewPath}/${student.studentNumber}`
                                                : `/home${viewPath}/${student.studentNumber}`;

                                            navigate(targetPath);
                                        }}
                                    />
                                )}
                            />
                        </>
                    )}
                </div>
            )}
            <DcsdDialog
                actions={
                    <div className="modal-btn-group">
                        <ActionButton
                            className="action-button-cancel"
                            label="Cancel"
                            onClick={() => setOpen("false")}
                        />
                        <ActionButton
                            className="action-button-reg"
                            label="Confirm Release"
                            onClick={() => {
                                handleToggleRelease();
                            }}
                        />
                    </div>
                }
                ariaLabel="Confirmation Release Epr Dialog"
                hasCloseX={false}
                id="confirm-release"
                open={open}
                title="Release Report Card Confirmation"
            >
                <div>
                    Please confirm that you have completed all work on the progress reports for your students and are
                    ready to queue them for release to families on{" "}
                    <b>{formatDateAndTime(currentParentWindow?.startDate, true)}</b>.
                    <div style={{ marginTop: "16px" }}>
                        If you need to make changes after executing the release, you may cancel the release until the{" "}
                        teacher window expires on <b>{formatDateAndTime(currentGradingProfileWindow?.endDate, true)}</b>
                        {"."}
                    </div>
                    <div style={{ marginTop: "16px" }}>
                        Please note that PDFs of each student&apos;s progress report{" "}
                        <u>may take up to 24 hours to generate</u> before becoming available for download.
                    </div>
                </div>
            </DcsdDialog>

            <DcsdDialog
                actions={
                    <div className="modal-btn-group">
                        <ActionButton
                            className="action-button-cancel"
                            label="Cancel"
                            onClick={() => setOpen("false")}
                        />
                        <ActionButton
                            className="action-button-reg"
                            label="Confirm Cancel Release"
                            onClick={() => {
                                handleToggleRelease();
                            }}
                        />
                    </div>
                }
                ariaLabel="Confirmation Cancel Release Epr Dialog"
                hasCloseX={false}
                id="confirm-release-cancel"
                open={open}
                title="Cancel Report Card Release Confirmation"
            >
                <div>
                    Please confirm that you wish to cancel the release of the progress reports for your students. Once
                    you have completed your update/changes, please plan to release the progress reports again so they
                    are available to families on <b>{formatDateAndTime(currentParentWindow?.startDate, true)}</b>.
                </div>
                <div style={{ marginTop: "16px" }}>
                    Please note that PDFs of each student&apos;s progress report{" "}
                    <u>may take up to 24 hours to re-generate</u> before becoming available for download.
                </div>
            </DcsdDialog>

            <DcsdDialog
                actions={
                    <div className="modal-btn-group">
                        <ActionButton
                            className="action-button-cancel"
                            label="Cancel"
                            onClick={() => setOpen("false")}
                        />
                        <ActionButton
                            className="action-button-reg"
                            label="Confirm"
                            onClick={() => {
                                handleDelete();
                            }}
                        />
                    </div>
                }
                ariaLabel="Confirmation Delete Epr Dialog"
                hasCloseX={false}
                id="confirm-delete-homeroom-epr"
                open={open}
                title="Delete Report Card Confirmation"
            >
                <div>
                    Please confirm you would like to delete your homeroom Report Card.{" "}
                    <span style={{ fontStyle: "italic", textDecoration: "underline" }}>
                        Please note, this action cannot be undone and will result in the loss of all previously entered
                        data, including student ratings and student comments.
                    </span>
                </div>
            </DcsdDialog>
            {state.loader && <LoadingSvg />}
        </div>
    );
};

GradingAccordionItem.propTypes = {
    dispatch: PropTypes.func.isRequired,
    grade: PropTypes.shape({
        subjectToStandards: PropTypes.arrayOf(
            PropTypes.shape({
                templateId: PropTypes.number.isRequired,
                subjectName: PropTypes.string.isRequired
            })
        ).isRequired
    }),
    gradingProfile: PropTypes.shape({
        key: PropTypes.string.isRequired,
        grade: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        gradeName: PropTypes.string.isRequired,
        gradingProfileStudents: PropTypes.array.isRequired,
        gradingCriteriaList: PropTypes.array.isRequired,
        homeroomGroups: PropTypes.array.isRequired,
        status: PropTypes.string.isRequired,
        comment: PropTypes.string,
        timePeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        schoolYearKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    }).isRequired,
    isHistoricalView: PropTypes.bool,
    ownerDisplayName: PropTypes.string.isRequired,
    setSavedMessage: PropTypes.func.isRequired,
    setShowSaved: PropTypes.func.isRequired,
    state: PropTypes.object.isRequired,
    basePath: PropTypes.string
};

export default GradingAccordionItem;
